/**
 * Constants for AI Notes session management
 * Error codes and lock reasons for error handling
 */

export const ERROR_CODES = {
  NOTE_LOCKED_EHR_SUBMITTED: 'NOTE_LOCKED_EHR_SUBMITTED',
  NOTE_LOCKED_SIGNED: 'NOTE_LOCKED_SIGNED',
  // WebSocket recording error codes
  SESSION_ALREADY_RECORDING: 'SESSION_ALREADY_RECORDING',
  // Auth error codes (from backend 401 responses)
  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',
  TRANSCRIPT_QUALITY_TOO_LOW: 'TRANSCRIPT_QUALITY_TOO_LOW',
  UNSUPPORTED_TEMPLATE: 'UNSUPPORTED_TEMPLATE',
  UNSUPPORTED_SESSION_TYPE: 'UNSUPPORTED_SESSION_TYPE',
  UNSUPPORTED_TEMPLATE_AUTO_FILL: 'UNSUPPORTED_TEMPLATE_AUTO_FILL',
  // Connection health error codes
  NO_AUDIO_DETECTED: 'NO_AUDIO_DETECTED',
  HEARTBEAT_TIMEOUT: 'HEARTBEAT_TIMEOUT',
};

export const LOCK_REASONS = {
  EHR_SUBMITTED: 'EHR_SUBMITTED',
  NOTE_SIGNED: 'NOTE_SIGNED',
  BACKEND_LOCKED: 'BACKEND_LOCKED',
};

export const ERROR_MESSAGES = {
  LOCK_ERROR: {
    message: 'AI Notes Locked',
    description: 'AI notes have already been generated and sent to EHR. Cannot regenerate locked notes.',
  },
  NETWORK_ERROR: {
    message: 'Network Error',
    description: 'Failed to trigger AI notes generation.',
  },
  SESSION_ALREADY_RECORDING: {
    message: 'Recording Already In Progress',
    description: 'A recording session is already active for this appointment. Please close other browser tabs or windows that may be recording.',
  },
  AUTH_TOKEN_MISSING: {
    message: 'Authentication Required',
    description: 'Please log in again to continue recording.',
  },
  AUTH_TOKEN_INVALID: {
    message: 'Session Expired',
    description: 'Your session has expired. Please log in again.',
  },
  CONNECTION_LOST: {
    message: 'Connection Lost',
    description: 'Trying to reconnect...',
  },
  RECONNECT_FAILED: {
    message: 'Connection Failed',
    description: 'Unable to reconnect after multiple attempts. Recording has been stopped.',
  },
  INTERNET_DISCONNECTED: {
    message: 'Internet Connection Lost',
    description: 'Recording has been paused. It will resume automatically when connection is restored.',
  },
  RECORDING_PAUSED_NO_CONNECTION: {
    message: 'No Connection',
    description: 'Cannot start or resume recording without internet connection.',
  },
  TRANSCRIPT_QUALITY_TOO_LOW: {
    message: 'Recording Quality Too Low',
    description: 'The recording does not contain enough clinical content to generate notes. Please ensure there is clear clinical conversation in the recording.',
  },
  UNSUPPORTED_TEMPLATE: {
    message: 'Template Not Supported',
    description: 'Your EHR note uses a template that is not currently supported by AI note generation. Please contact your administrator.',
  },
  UNSUPPORTED_SESSION_TYPE: {
    message: 'Unsupported Appointment Type',
    description: 'This appointment type is not supported for recording or AI note generation.',
  },
  NO_AUDIO_DETECTED: {
    message: 'No Audio Detected',
    description: 'No transcript data has been received. Please check your microphone and ensure you\'re speaking. Recording will continue, but you may want to stop and restart if the issue persists.',
  },
  HEARTBEAT_TIMEOUT: {
    message: 'Connection Health Check Failed',
    description: 'The connection appears to be inactive. Attempting to reconnect...',
  },
  NO_CLIENT_AUDIO: {
    message: 'Recording Stopped',
    description: 'No audio was detected for an extended period. Click record to start again.',
  },
  STREAM_RECONNECTING: {
    message: 'Refreshing Connection',
    description: 'Briefly refreshing the transcription stream...',
  },
  WEBSOCKET_DISCONNECTED: {
    message: 'Recording Connection Lost',
    description: 'The connection to the recording server was lost and could not be restored. Please stop the recording and start again.',
  },
  WEBSOCKET_NOT_CONNECTED: {
    message: 'Unable to Connect',
    description: 'Could not establish a connection to the recording server. Please check your network and try starting the recording again.',
  },
  TRANSCRIPT_INACTIVITY: {
    message: 'No Transcript Activity',
    description: 'No new transcript has been received recently. Please check your microphone or ensure you\'re speaking. Transcription will automatically resume when audio is detected.',
  },
  SERVER_ERROR: {
    message: 'AI note generation failed',
    description: 'Something went wrong on our end. Please try again.',
  },
};

export const WARNING_MESSAGES = {
  UNSUPPORTED_TEMPLATE_AUTO_FILL: {
    message: 'Warning: Potential data loss.',
    description: 'The previous note uses an unsupported template due to which the current note might not be able to port all fields from the previous template in appropriate format.',
  },
};

export const TOOLTIP_TEXT = {
  NOTE_LOCKED: 'Note locked - already sent to EHR',
  RECORDING_DISABLED_NO_CONNECTION: 'Cannot record without internet connection',
  GENERATE_DISABLED_NO_CONNECTION: 'Cannot generate AI notes without internet connection',
  UPLOAD_DISABLED_NO_CONNECTION: 'Cannot upload transcript without internet connection',
  INVALID_APPOINTMENT: 'This appointment type is not supported for recording or AI note generation',
};
